import React, {
	useState,
	useEffect,
	useContext,
	useMemo,
	useCallback,
	lazy,
	Suspense,
} from 'react';
import withAppContext from 'tbk-components/src/hooks/withAppContext';
import { useHashAsState } from 'tbk-components/src/hooks';
import { ICareersFilterProps } from '../CareerCardGroup';
import Select from '../../common/ReactSelect';
import { memoizeOptionsForReactSelect } from '../CareerCardGroup';
const DatePicker = lazy(() => import('react-datepicker'));
import RecruiterChart, { IRecruiterChartDataPoint } from './RecruiterChart';
import moment from 'moment';
import { debounce } from 'lodash';
import qs from 'qs';
import { ILink } from 'tbk-components/src/components/Button';
import Loading from 'tbk-components/src/components/Loading';
import RecruiterDataTable, {
	IRecruiterDataTableI18n,
	IRecruiterDataTableItem,
	IRecruiterDataTablePersonalSettings,
} from './RecruiterDataTable';

export interface IRecruiterDashboardData {
	buckets: IRecruiterChartDataPoint[];
	total: number;
	hits: IRecruiterDataTableItem[];
}

export interface IRecruiterDashboardAssets {
	i18n?: {
		title?: string;
		from?: string;
		to?: string;
		search?: string;
		filters?: ICareersFilterProps[];
		chart?: {
			labels?: {
				date?: string;
				numberOfInterations?: string;
				numberOfApplicants?: string;
			}
		},
		table?: IRecruiterDataTableI18n;
	};
}

export interface IRecruiterDashboardProps {
	defaultAssets: IRecruiterDashboardAssets;
	context?: any;
}

export const stringToDate = (string: string) => {
	if (!string) {
		return null;
	}
	const m = moment(string);
	return m.isValid() ? m.toDate() : null;
};
export const dateToString = (date: Date) => {
	if (!date) {
		return null;
	}
	const m = moment(date);
	return m.isValid() ? m.format('YYYY-MM-DD') : null;
};

const RecruiterDashboard: React.FC<IRecruiterDashboardProps> = withAppContext(
	({ defaultAssets, context }) => {
		const { Api } = useContext<{ Api: any }>(context);
		const [assets] = useState<IRecruiterDashboardAssets>(defaultAssets);
		const [tablePersonalSettings, setTablePersonalSettings] =
			useState<IRecruiterDataTablePersonalSettings>(null);
		const [personalSettingsChanged, setPersonalSettingsChanged] =
			useState(false);
		const [busy, setBusy] = useState(true);
		const [data, setData] = useState<IRecruiterDashboardData>(null);
		const [clickedDate,setClickedDate] = useState<Date>(null);
		const filters = assets?.i18n?.filters || [];
		const filterHash = JSON.stringify(filters);
		const dynamicParameters = useMemo(() => {
			const filters = JSON.parse(filterHash);
			const dynamicParameters = {};
			(filters || []).forEach((filter: ICareersFilterProps) => {
				dynamicParameters[filter.taxonomy] = 'csv';
			});
			return dynamicParameters;
		}, [filterHash]);
		const [ filterOptions, searchConfig ] = useMemo(
			() => memoizeOptionsForReactSelect(filters),
			[filters],
		);
		const [state, normalizedState, updateState] = useHashAsState({
			parameters: {
				s: 'string',
				from: 'string',
				to: 'string',
				...dynamicParameters,
			},
			defaults: {},
		});

		const onChangeFilter = (taxonomy: string, value: string | string[]) => {
			updateState({
				[taxonomy]: Array.isArray(value) ? value : [value],
			});
		};

		const updateDate = (date: Date, which: 'from' | 'to') => {
			updateState({
				[which]: dateToString(date),
			});
		};
		const fromDate = stringToDate(state.from),
			toDate = stringToDate(state.to),
			today = new Date();

		useEffect(() => {
			Api.getRecruiterTablePersonalSettings().then(
				setTablePersonalSettings,
			);
		}, []);

		const persistRecruiterTablePersonalSettings = useCallback(
			debounce((settings: IRecruiterDataTablePersonalSettings) => {
				Api.persistRecruiterTablePersonalSettings(settings);
			}, 2000),
			[],
		);

		useEffect(() => {
			// This check prevents an immediate postback when we get the settings from the server
			if (personalSettingsChanged) {
				persistRecruiterTablePersonalSettings(tablePersonalSettings);
			}
		}, [tablePersonalSettings, personalSettingsChanged]);

		useEffect(() => {
			setBusy(true);
			setClickedDate(null);
			const state = qs.parse(normalizedState.replace(/^#!/, ''));
			Api.getRecruiterDashboardData(state)
				.then((results) => {
					setData(results);
				})
				.finally(() => {
					setBusy(false);
				});
		}, [normalizedState]);

		const onChangePersonalSettings = useCallback(
			(change: IRecruiterDataTablePersonalSettings) => {
				setPersonalSettingsChanged(true);
				setTablePersonalSettings((settings) => ({
					...settings,
					...change,
				}));
			},
			[],
		);
		const onClickDownload = (args) => {
			const url = Api.getUri('downloadRecruiterDashboardData') + '?' + qs.stringify( {...state,...args} );
			window.open( url, '_blank' );
		};

		return (
			<div className="calian-recruiter-dashboard bg-light-bg pt-2 pt-sm-10">
				<div className="calian-recruiter-dashboard__container container-fluid mt-1 mt-sm-0 pt-lg-8">
					<div className="row mb-6">
						<div className="col-12">
							<h1 className="h2 mb-0">{assets.i18n.title}</h1>
						</div>
					</div>
				</div>
				<div className="container-fluid">
					<div className="row">
						<div className="calian-recruiter-dashboard__filters col-12 py-4">
							<div className="calian-recruiter-dashboard__container container-fluid">
								<Suspense fallback={null}>
									<div className="row">
										<div className="col-12 col-sm-3 mb-3">
											<DatePicker
												selected={fromDate}
												onChange={(date: Date) =>
													updateDate(date, 'from')
												}
												selectsStart
												startDate={fromDate}
												endDate={toDate}
												maxDate={today}
												placeholderText={
													assets.i18n.from
												}
												isClearable
											/>
										</div>
										<div className="col-12 col-sm-3 mb-3">
											<DatePicker
												selected={toDate}
												onChange={(date: Date) =>
													updateDate(date, 'to')
												}
												selectsEnd
												startDate={fromDate}
												endDate={toDate}
												minDate={fromDate}
												maxDate={today}
												placeholderText={assets.i18n.to}
												isClearable
											/>
										</div>
										{filters.map(
											(filter: ICareersFilterProps) => {
												if ( state.career && ! ['career','metric'].includes( filter.taxonomy ) ) {
													// The server ignores the other filters when career is selected, so let's hide them from the UI.
													return null;
												}
												if ( state.metric?.length && ['registrations_per_day','logins_per_day'].includes( state.metric[0] ) ) {
													// These metrics are only concerned with users, not careers, so we'll hide the career taxonomies.
													if ( 'metric' !== filter.taxonomy ) {
														return null;
													}
												}

												// Accommodating multiple values.
												const value = ( state[filter.taxonomy] || [] ).map( v => filterOptions[filter.taxonomy][v] );

												return (
													<div
														className="col-12 col-sm-3 mb-mb-0 mb-3"
														key={filter.taxonomy}
													>
														<div className="calian-recruiter-dashboard__filter-dropdown">
															<Select
																className="form-select react-form-select"
																classNamePrefix="react-select"
																isSearchable
																isClearable
																isMulti={
																	'metric' !==
																	filter.taxonomy
																}
																placeholder={
																	filter.label
																}
																options={Object.values(
																	filterOptions[
																		filter
																			.taxonomy
																	],
																)}
																onChange={(
																	o: any,
																) => {
																	onChangeFilter(
																		filter.taxonomy,
																		Array.isArray(
																			o,
																		)
																			? o.map(
																					(
																						v,
																					) =>
																						v.value,
																			  )
																			: o?.value,
																	);
																}}
																aria-label={
																	filter.label
																}
																value={value}
																filterOption={searchConfig as (option: any, rawInput: string) => boolean}
															/>
														</div>
													</div>
												);
											},
										)}
									</div>
								</Suspense>
							</div>
						</div>
					</div>
				</div>
				<div className="calian-recruiter-dashboard__container container-fluid mb-0 pb-1 pb-sm-5 pb-lg-10 pt-lg-5">
					<div className="row mb-6">
						<div className="col-12 min-height-350px">
							{busy ? (
								<Loading />
							) : (
								<RecruiterChart
									data={data?.buckets || []}
									labels={{
										xAxis: assets?.i18n?.chart?.labels?.date,
										yAxis: assets?.i18n?.chart?.labels[ ['registrations_per_day','logins_per_day'].indexOf(state.metric) !== -1 ? 'numberOfApplicants' : 'numberOfInterations' ]
									}}
									onClick={(dataPoint) => {
										if ( dataPoint.key === dateToString( clickedDate ) ) {
											setClickedDate( null );
										} else {
											setClickedDate( stringToDate(dataPoint.key) );
										}
									}}
								/>
							)}
						</div>
					</div>
					<div className="row">
						<div className="calian-recruiter-dashboard__data-table-container col-12">
							{busy ? null : (
								<RecruiterDataTable
									data={data?.hits || []}
									i18n={assets?.i18n?.table}
									settings={tablePersonalSettings}
									metric={state.metric ? state.metric[0] : 'any_interaction'}
									onChangePersonalSettings={
										onChangePersonalSettings
									}
									onClickDownload={onClickDownload}
									selectedDate={clickedDate}
									fromDate={fromDate || moment().subtract(1,'month').toDate()}
									toDate={toDate || today}
								/>
							)}
						</div>
					</div>
				</div>
			</div>
		);
	},
);

export default RecruiterDashboard;
